import { useMemo, useCallback, memo } from "react";
import filters from "byzantine/src/filters";
import { format } from "date-fns";
import { useBreakpoints } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";

import Table, { Column } from "../../Table";

import styles from "./SubscriptionsTable.module.scss";
import MerchantIcon from "../MerchantIcon";

type TableTransaction = API.CardTransaction & {
  card: string;
};

const columns: Column<TableTransaction>[] = [
  {
    header: "DATE",
    accessor: "transaction_date",
    className: styles.dateCol,
    render: (date: unknown) => format(new Date(date as string), "MMM d"),
  },
  {
    header: "MERCHANT",
    accessor: "merchant_name",
    render: (merchantName) => (
      <div className={styles.merchantCell}>
        <div className={styles.logoWrapper}>
          <MerchantIcon name={merchantName || ""} size="s" />
        </div>
        <span>{merchantName}</span>
      </div>
    ),
  },
  {
    header: "CARD",
    accessor: "card",
    hideAt: ["xs", "s", "m"],
    className: styles.cardCol,
  },
  {
    header: "AMOUNT",
    accessor: "amount",
    render: (amount: unknown) =>
      filters.currency(filters.dollarsToPennies(amount as Dollars)),
    className: styles.amountCol,
  },
];

interface SubscriptionsTableProps {
  transactions?: API.CardTransaction[];
  cardName: string;
  onRowClick?: (tx: API.CardTransaction) => void;
}

const SubscriptionsTable = ({
  transactions,
  cardName,
  onRowClick,
}: SubscriptionsTableProps) => {
  const { l10n } = useLocalization();
  const { m } = useBreakpoints();

  const emptyMessage = l10n.getString(
    "subscription-table-empty",
    null,
    "You have no recurring card transactions for this card."
  );

  const handleRowClick = useCallback(
    (transaction: API.CardTransaction) => {
      onRowClick?.(transaction);
    },
    [onRowClick]
  );

  const data: TableTransaction[] = useMemo(
    () =>
      transactions?.map((transaction) => ({
        ...transaction,
        card: cardName,
      })) || ([] as TableTransaction[]),
    [cardName, transactions]
  );

  if (data.length === 0) {
    return <div className={styles.empty}>{emptyMessage}</div>;
  }

  return (
    <div className={styles.container}>
      <Table<TableTransaction>
        columns={columns}
        data={data}
        className={styles.transactionsTable}
        isHeaderVisible={m}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default memo(SubscriptionsTable);
